import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useVendorHelp } from "@/hooks/useVendorHelp";
import { Skeleton } from "@/components/ui/skeleton";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { VendorSidebar } from "@/components/admin/dashboard/VendorSidebar";
import { useVendorService } from "@/hooks/useVendorService";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useServiceEditAccess } from "@/hooks/useServiceEditAccess";
import { Calendar, BookOpen, Star, Pencil, Settings, Bell, HelpCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const VendorHelp = () => {
  const { vendorHelp, isLoading } = useVendorHelp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get serviceId from URL params or query string
  const serviceId = id || searchParams.get('serviceId');
  const { loading: serviceLoading, serviceName } = useVendorService(serviceId);
  const { showEditInSidebar } = useServiceEditAccess(serviceId || "");
  const isMobile = useIsMobile();

  // Service menu items for mobile navigation
  const serviceMenuItems = [
    { view: "calendar", label: "Calendar", icon: Calendar, visible: true },
    { view: "bookings", label: "Bookings", icon: BookOpen, visible: true },
    { view: "reviews", label: "Reviews", icon: Star, visible: true },
    { view: "edit", label: "Edit Service", icon: Pencil, visible: showEditInSidebar },
    { view: "settings", label: "Calendar Settings", icon: Settings, visible: true },
    { view: "notifications", label: "Notifications", icon: Bell, visible: true },
    { view: "help", label: "Help", icon: HelpCircle, visible: true }
  ];

  const handleViewChange = (view: string) => {
    if (view === "help") {
      // Already on help page
      return;
    }
    // Navigate to other service views using React Router
    navigate(`/service/${serviceId}/${view}`);
  };
  
  if (serviceLoading || !serviceId) {
    return <div>Loading...</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        <Header 
          isServiceDashboard={true}
          serviceMenuItems={serviceMenuItems}
          onServiceViewChange={handleViewChange}
          activeServiceView="help"
        />
        
        <div className="flex flex-1 mt-16 w-full">
          {!isMobile && (
            <VendorSidebar
              activeView="help"
              serviceId={serviceId || ""}
              onViewChange={handleViewChange}
            />
          )}
          
          <div className="flex-1 p-4 md:p-6">
            <div className="mb-6 flex items-center gap-4">
              {!isMobile && <SidebarTrigger className="md:hidden" />}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{serviceName}</h2>
                <h3 className="text-xl font-semibold text-gray-700">Help</h3>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : (
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: vendorHelp?.content || '<p>No help content available.</p>' 
                  }}
                />
              )}
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </SidebarProvider>
  );
};

export default VendorHelp;